import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FaTwitter,
  FaInstagram,
  FaSearch,
  FaUndo,
  FaChevronDown,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./FilterBar.css";
import countriesData from "../../data/countries.json";
import placesData from "../../data/places.json";


function CustomSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.trim().toLowerCase())
  );
   
  

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="location-custom-select" ref={dropdownRef}>
      <div
        className="location-select-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <FaChevronDown />
      </div>
      {isOpen && (
        <div className="location-select-dropdown">
          <input
            type="text"
            className="location-select-search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div className="location-select-options">
            <div
              className={`location-select-option ${!value ? "selected" : ""}`}
              onClick={() => {
                onChange("");
                setIsOpen(false);
                setSearch("");
              }}
            >
              {placeholder}
            </div>
            {filteredOptions.map((option, index) => (
  <div
    key={option.value + index}  // Appending index to make key unique
    className={`location-select-option ${
      value === option.value ? "selected" : ""
    }`}
    onClick={() => {
      onChange(option.value);
      setIsOpen(false);
      setSearch("");
    }}
  >
    {option.label}
  </div>
))}

          </div>
        </div>
      )}
    </div>
  );
}

function FilterBar({ onLocationSelect, setFetchedData }) {
  const [platform, setPlatform] = useState(localStorage.getItem("platform") || "");
  const [country, setCountry] = useState(localStorage.getItem("country") || "");
  const [place, setPlace] = useState(localStorage.getItem("place") || "");
  const [startDate, setStartDate] = useState(localStorage.getItem("startDate") ? new Date(localStorage.getItem("startDate")) : null);
  const [endDate, setEndDate] = useState(localStorage.getItem("endDate") ? new Date(localStorage.getItem("endDate")) : null);
  const [keyword, setKeyword] = useState(localStorage.getItem("keyword") || "");
  const [isLoading, setIsLoading] = useState(false);
  const [filteredPlaceOptions, setFilteredPlaceOptions] = useState([]);
  
  useEffect(() => {
    localStorage.setItem("platform", platform);
  }, [platform]);
  
  useEffect(() => {
    localStorage.setItem("country", country);
  }, [country]);
  
  useEffect(() => {
    localStorage.setItem("place", place);
  }, [place]);
  
  useEffect(() => {
    localStorage.setItem("startDate", startDate ? startDate.toISOString() : "");
  }, [startDate]);
  
  useEffect(() => {
    localStorage.setItem("endDate", endDate ? endDate.toISOString() : "");
  }, [endDate]);
  
  useEffect(() => {
    localStorage.setItem("keyword", keyword);
  }, [keyword]);
  
 


  const countryOptions = countriesData.countries.map((c) => ({
    value: c.name,
    label: c.name,
    id: c.id,
    latitude: c.latitude,
    longitude: c.longitude,
  }));

  const formatPlaceName = (placeCountry) => {
    // Example parsing: split by commas or take up to the first comma
    const parts = placeCountry.split(','); // Splits the string by commas
    return parts[0].trim(); // Returns the first part, trimming any extra whitespace
  };

  const getFilteredPlaces = (countryName) => {
    return placesData.places.filter((p) =>
      p.place_country.toLowerCase().includes(countryName.toLowerCase())
    );
  };


  useEffect(() => {
    if (country) {
      const placesForCountry = getFilteredPlaces(country);
      setFilteredPlaceOptions(
        placesForCountry.map((p) => ({
          value: p.place_country,
          label: formatPlaceName(p.place_country), // Use the formatted name
          id: p.id,
          latitude: p.latitude,
          longitude: p.longitude,
        }))
      );
    } else {
      setFilteredPlaceOptions([]);
    }
  }, [country]);

  useEffect(() => {
    if (place) {
      const selectedPlace = placesData.places.find(
        (p) => p.place_country === place
      );
      if (selectedPlace) {
        onLocationSelect({
          coordinates: [selectedPlace.longitude, selectedPlace.latitude],
          zoom: 11,
        });
      }
    } else if (country) {
      const selectedCountry = countriesData.countries.find(
        (c) => c.name === country
      );
      if (selectedCountry) {
        onLocationSelect({
          coordinates: [selectedCountry.longitude, selectedCountry.latitude],
          zoom: 5,
        });
      }
    }
  }, [country, place, onLocationSelect]);

  const resetFilters = () => {
    // Clear localStorage
    localStorage.removeItem("platform");
    localStorage.removeItem("country");
    localStorage.removeItem("place");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("keyword");
    localStorage.removeItem("selectedLocation");
    localStorage.removeItem("fetchedData");
  
    // Reset state
    setPlatform("");
    setCountry("");
    setPlace("");
    setStartDate(null);
    setEndDate(null);
    setKeyword("");
    setFetchedData([]); // Clear fetchedData so markers are removed
    onLocationSelect(null);
  
    // Reset map zoom level to 1.5 after resetting filters
    onLocationSelect({
      coordinates: [0, 0], // Reset to some central coordinates (e.g., 0, 0 or any default location)
      zoom: 1.5, // Set zoom level to 1.5
    });
  };
  
  
  

  const handleSearch = async () => {
    if (!platform || !country) {
      console.log("Please fill all required fields before searching.");
      return;
    }
  
    setIsLoading(true);
  
    const selectedCountry = countryOptions.find((c) => c.value === country);
    const selectedPlace = filteredPlaceOptions.find((p) => p.value === place);
  
    const filters = {
      platform, // ✅ Send platform explicitly
      keyword,
      ...(selectedCountry && {
        country_id: selectedCountry.id,
        country_name: selectedCountry.value,
        country_latitude: selectedCountry.latitude,
        country_longitude: selectedCountry.longitude,
      }),
      ...(selectedPlace && {
        place_id: selectedPlace.id,
        place_name: selectedPlace.value,
        place_latitude: selectedPlace.latitude,
        place_longitude: selectedPlace.longitude,
      }),
      ...(startDate && { from_date: startDate.toISOString().split("T")[0] }),
      ...(endDate && { to_date: endDate.toISOString().split("T")[0] }),
    };
  
    console.log("Sending filters:", filters);
  
    try {
      const platformRoute = platform === "instagram" ? "instagram" : "twitter";
      const url = `api/${platformRoute}/location/`;
      console.log("Request URL:", url);
  
      const response = await axios.post(url, filters);
      if (response.status === 200) {
        console.log("Response from backend:", response.data);
  
        // Sort data from newest to oldest
        const sortedData = response.data.sort((a, b) => {
          const aTime = a.timestamp || a.created_at || a.created || 0;
          const bTime = b.timestamp || b.created_at || b.created || 0;
          return bTime - aTime; // Newest first
        });
  
        setFetchedData(sortedData); // Set sorted data to state
      } else {
        console.error("Unexpected response status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  
    setIsLoading(false);
  };
  


  return (
    <div className="location-filter-bar">
      {/* Filter UI Components */}
      <div className="location-platform-buttons-main">
      <h3>Choose Platform</h3> 
      <div className="location-platform-buttons">
      
        <button
          className={`location-platform-btn ${
            platform === "twitter" ? "active" : ""
          }`}
          onClick={() => setPlatform(platform === "twitter" ? "" : "twitter")}
        >
          <FaTwitter /> Twitter
        </button>
        <button
          className={`location-platform-btn ${
            platform === "instagram" ? "active" : ""
          }`}
          onClick={() =>
            setPlatform(platform === "instagram" ? "" : "instagram")
          }
        >
          <FaInstagram /> Instagram
        </button>
      </div>
      </div>
      {platform && (  // This conditional rendering block ensures the rest of the UI is only displayed when a platform is selected
      <>

      <div className="location-filter-select-container">
      <h3>Select Country</h3>
        <CustomSelect
          options={countryOptions}
          value={country}
          onChange={(value) => {
            setCountry(value);
            setPlace(""); // Reset place when country changes
          }}
          placeholder="Select Country"
          searchPlaceholder="Search countries..."
        />
      </div>

      <div className="location-filter-select-container">
      <h3>Select Place</h3>
        <CustomSelect
          options={filteredPlaceOptions}
          value={place}
          onChange={setPlace}
          placeholder="Select Place"
          searchPlaceholder="Search places..."
        />
      </div>

      {/* Date Filter */}
      <div className="location-date-filters-main">
      <h3>Date Range</h3> 
      <div className="location-date-filters">
      
        <DatePicker
          selected={startDate}
          onChange={(date) => setStartDate(date)}
          placeholderText="From Date"
          className="location-date-picker"
          maxDate={endDate || new Date()}
        />
        <DatePicker
          selected={endDate}
          onChange={(date) => setEndDate(date)}
          placeholderText="To Date"
          className="location-date-picker"
          minDate={startDate}
          maxDate={new Date()}
        />
      </div>
      </div>

     {/* Search Field (Only shown for Twitter) */}
     {platform === "twitter" && (
            <div className="location-search-container-main">
              <h3>Keyword</h3>
              <div className="location-search-container">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="Search keyword..."
                  className="location-search-input"
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
            </div>
          )}

          {/* Search Button (Always shown for both platforms) */}
          <div className="location-search-btn-container">
            <h3>Search Posts</h3>
            <button
              className="location-search-btn"
              onClick={handleSearch}
              disabled={isLoading} // Disable the button while loading
              style={{
                background: isLoading ? "#ccc" : "", // Change button color when loading
                cursor: isLoading ? "not-allowed" : "pointer", // Disable cursor when loading
                display: "flex", // To align the icon or loader properly
                alignItems: "center", // Vertically center the icon or loader
                justifyContent: "center", // Horizontally center the icon or loader
              }}
            >
              {isLoading ? (
                <div
                  className="loader-small"
                  style={{ borderTopColor: "#fff" }}
                />
              ) : (
                <FaSearch style={{ fontSize: "20px", color: "#fff" }} /> // Show search icon
              )}
            </button>
          </div>
      <div className="location-search-container-main">
      <h3>Reset all filters</h3> 
      {/* Reset Button */}
      <button className="location-reset-btn" onClick={resetFilters}>
  <FaUndo />
</button>
    </div>
    </>
    )}
    </div>
  );
}

export default FilterBar;
